import React, { forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
    xs: { padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-2"]}`, fontSize: tokens.typography.fontSize["md-12px"], lineHeight: "16px", borderRadius: "6px" },
    sm: { padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-2-5"]}`, fontSize: tokens.typography.fontSize["lg-14px"], lineHeight: "20px", borderRadius: "6px" },
    md: { padding: `${tokens.globals["spacing-1-5"]} ${tokens.globals["spacing-2-5"]}`, fontSize: tokens.typography.fontSize["lg-14px"], lineHeight: "20px", borderRadius: "7px" },
    lg: { padding: `${tokens.globals["spacing-2"]} ${tokens.globals["spacing-2-5"]}`, fontSize: tokens.typography.fontSize["lg-14px"], lineHeight: "20px", borderRadius: "7px" },
};

const defaultFocusVisibleState = {
    outline: `${tokens.globals["spacing-0-5"]} solid ${tokens.colors.brand.blue["500"]}`,
    outlineOffset: tokens.globals["spacing-0-5"],
};

const errorState = {
    outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.states.alert.default}`,
    boxShadow: "0px 0px 1px 1px rgba(196, 61, 81, 0.20)",
};

/**
 * The three CSS custom properties a tenant's brand config sets on `document.body` once
 * `GetBrandColor` resolves (see Header.js) - they supersede every variant's own background,
 * on every variant, not just primary. `:root` in tokens.css/custom.css already declares
 * defaults for these, so the `var(..., fallback)` below is a belt-and-braces fallback for a
 * page where those root defaults are ever missing, not the normal path.
 */
function brandBackground(cssVar, fallback) {
    return `var(${cssVar}, ${fallback})`;
}

const variantConfig = {
    primary: {
        background: brandBackground("--button-color", tokens.colors.brand["aie-blue"]["500"]),
        color: tokens.colors.neutral.white,
        hover: { background: brandBackground("--button-hover-color", tokens.colors.brand["aie-blue"]["600"]) },
        active: { background: brandBackground("--button-active-color", tokens.colors.brand["aie-blue"]["700"]), transform: "scale(0.96)" },
    },

    secondary: {
        background: tokens.colors.brand.blue["100"],
        color: tokens.colors.brand["aie-blue"]["500"],
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["100"]}`,
        outlineOffset: `-${tokens.globals["spacing-px"]}`,
        hover: { background: brandBackground("--button-hover-color", tokens.colors.brand.blue["200"]) },
        active: { background: brandBackground("--button-active-color", tokens.colors.brand.blue["300"]), transform: "scale(0.96)" },
    },

    destructive: {
        background: tokens.colors.states.alert["50"],
        color: tokens.colors.states.alert.default,
        hover: { background: brandBackground("--button-hover-color", tokens.colors.states.alert["100"]) },
        active: { background: brandBackground("--button-active-color", tokens.colors.states.alert["50"]), transform: "scale(0.96)" },
    },
};

const getVariantStyles = (variant) => {
    const { background, color, outline, outlineOffset, hover, active } = variantConfig[variant] || variantConfig.primary;

    return {
        background,
        color,
        border: "none",
        outline,
        outlineOffset,
        "&:hover:not(:disabled)": hover,
        "&:active:not(:disabled)": active,
        "&:focus-visible:not(:disabled)": defaultFocusVisibleState,
        '&[data-error="true"]': errorState,
    };
};

const StyledButton = styled.button(({ size = "lg", variant = "primary", $colorOverride }) => {
    const { padding, fontSize, lineHeight, borderRadius } = sizes[size];
    const { background, color: variantColor, border, ...variantStates } = getVariantStyles(variant);
    const color = $colorOverride || variantColor;

    return {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: tokens.typography.fontWeights.medium,
        cursor: "pointer",
        transition: "all 0.15s ease-in-out",
        background: "transparent",
        color: "inherit",

        "&:disabled": {
            opacity: 0.50,
            cursor: "not-allowed",
            transform: "none",
        },

        padding,
        fontSize,
        lineHeight,
        borderRadius,

        gap: size === "xs" || size === "sm" ? tokens.globals["spacing-1"] : tokens.globals["spacing-1-5"],

        background,
        color,
        border,
        ...variantStates,

        "&:focus": {
            outline: "none",
        },

        "&:link, &:visited, &:hover, &:active, &:focus": {
            textDecoration: "none !important",
            color: `${color} !important`,
        },
    };
});

/**
 * A reusable button component that supports multiple visual variants and sizes.
 * The button is styled using existing CSS tokens and matches the UI Kit design.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.variant="primary"] - Visual variant: "primary", "secondary", or "destructive"
 * @param {string} [props.size="lg"] - Size variant: "xs", "sm", "md", or "lg"
 * @param {boolean} [props.disabled=false] - If true, the button is disabled
 * @param {boolean} [props.error=false] - If true, applies error (invalid) styling
 * @param {string} [props.colorOverride] - Hardcodes the text/icon color, bypassing the variant's own. Only for cases needing an exact color outside the token system (see NegativeButton).
 * @param {React.ReactNode} props.children - Content to display inside the button
 * @param {React.Ref} ref - Forwarded ref to the underlying button element
 * @returns {JSX.Element} The rendered button
 */
const Button = forwardRef(
    ({ variant = "primary", size = "lg", disabled = false, error = false, colorOverride, children, ...props }, ref) => {
        return (
            <StyledButton
                ref={ref}
                variant={variant}
                size={size}
                disabled={disabled}
                $colorOverride={colorOverride}
                data-error={error ? "true" : undefined}
                {...props}
            >
                {children}
            </StyledButton>
        );
    }
);

Button.displayName = "Button";

export default Button;
